import { Fragment, useCallback, useEffect, useState } from "react";
import { format } from "date-fns";
import { ChevronDown, ChevronRight, Loader2 } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import api, { formatApiError } from "@/lib/api";

const PAGE_SIZE = 50;

const OUTCOME_STYLES = {
  success: "bg-ukgreen-light text-ukgreen border-transparent",
  denied: "bg-amber-100 text-amber-800 border-transparent",
  failure: "bg-red-50 text-red-700 border-transparent",
};

export default function AuditLogs() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState("all");
  const [outcomeFilter, setOutcomeFilter] = useState("all");
  const [actorFilter, setActorFilter] = useState("");
  const [page, setPage] = useState(0);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    api.get("/audit-logs/actions").then((r) => setActions(r.data.actions)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip: page * PAGE_SIZE, limit: PAGE_SIZE };
      if (actionFilter !== "all") params.action = actionFilter;
      if (outcomeFilter !== "all") params.outcome = outcomeFilter;
      if (actorFilter) params.actor = actorFilter;
      const { data } = await api.get("/audit-logs", { params });
      setItems(data.items);
      setTotal(data.total);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [page, actionFilter, outcomeFilter, actorFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const fmt = (v) => {
    try {
      return format(new Date(v), "dd MMM yyyy HH:mm:ss");
    } catch {
      return v;
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div data-testid="audit-logs-page" className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Audit log</h1>
        <p className="text-sm text-slate-600 mt-1">
          Immutable record of sign-ins, changes and denied actions. Audit entries cannot be edited or deleted.
        </p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <Select value={actionFilter} onValueChange={(v) => { setActionFilter(v); setPage(0); }}>
          <SelectTrigger data-testid="audit-action-filter" className="w-full sm:w-64 h-11">
            <SelectValue placeholder="Filter by action" />
          </SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all" data-testid="audit-action-all">All actions</SelectItem>
            {actions.map((a) => (
              <SelectItem key={a} value={a} data-testid={`audit-action-${a}`}>{a}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={outcomeFilter} onValueChange={(v) => { setOutcomeFilter(v); setPage(0); }}>
          <SelectTrigger data-testid="audit-outcome-filter" className="w-full sm:w-48 h-11">
            <SelectValue placeholder="Outcome" />
          </SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all" data-testid="audit-outcome-all">All outcomes</SelectItem>
            <SelectItem value="success" data-testid="audit-outcome-success">Success</SelectItem>
            <SelectItem value="denied" data-testid="audit-outcome-denied">Denied</SelectItem>
            <SelectItem value="failure" data-testid="audit-outcome-failure">Failure</SelectItem>
          </SelectContent>
        </Select>
        <Input
          data-testid="audit-actor-filter"
          placeholder="Filter by actor email…"
          value={actorFilter}
          onChange={(e) => { setActorFilter(e.target.value); setPage(0); }}
          className="h-11 sm:w-64"
        />
      </div>

      <div className="rounded-lg border border-border bg-white overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-slate-600" data-testid="audit-loading">
            <Loader2 className="h-5 w-5 animate-spin" /> Loading audit log…
          </div>
        ) : items.length === 0 ? (
          <p className="py-16 text-center text-sm text-slate-600" data-testid="audit-empty">No audit entries found.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8"></TableHead>
                <TableHead>Time (UTC)</TableHead>
                <TableHead>Action</TableHead>
                <TableHead className="hidden md:table-cell">Actor</TableHead>
                <TableHead>Outcome</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((entry) => (
                <Fragment key={entry.id}>
                  <TableRow
                    data-testid={`audit-row-${entry.id}`}
                    className="cursor-pointer"
                    onClick={() => setExpanded(expanded === entry.id ? null : entry.id)}
                  >
                    <TableCell>
                      {expanded === entry.id ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </TableCell>
                    <TableCell className="font-mono text-xs text-slate-600">{fmt(entry.created_at)}</TableCell>
                    <TableCell className="font-medium text-slate-800 text-sm">{entry.action}</TableCell>
                    <TableCell className="hidden md:table-cell text-sm text-slate-600">{entry.actor_email || "system"}</TableCell>
                    <TableCell>
                      <Badge className={OUTCOME_STYLES[entry.outcome] || ""}>{entry.outcome}</Badge>
                    </TableCell>
                  </TableRow>
                  {expanded === entry.id && (
                    <TableRow data-testid={`audit-detail-${entry.id}`}>
                      <TableCell colSpan={5} className="bg-slate-50">
                        <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-xs py-2">
                          <div><dt className="text-slate-500">Actor role</dt><dd className="font-medium">{entry.actor_role || "—"}</dd></div>
                          <div><dt className="text-slate-500">Resource</dt><dd className="font-medium">{entry.resource_type || "—"} {entry.resource_id ? `(${entry.resource_id})` : ""}</dd></div>
                          <div><dt className="text-slate-500">IP address</dt><dd className="font-medium">{entry.ip || "—"}</dd></div>
                          <div className="sm:col-span-2 lg:col-span-1"><dt className="text-slate-500">Detail</dt><dd className="font-medium break-words">{entry.detail || "—"}</dd></div>
                          {entry.changes && Object.keys(entry.changes).length > 0 && (
                            <div className="sm:col-span-2 lg:col-span-4" data-testid={`audit-changes-${entry.id}`}>
                              <dt className="text-slate-500">Changes (previous → new)</dt>
                              <dd className="mt-1 space-y-1">
                                {Object.entries(entry.changes).map(([field, change]) => (
                                  <p key={field} className="font-mono break-words">
                                    <span className="text-slate-700 font-semibold">{field}</span>:{" "}
                                    <span className="text-red-700">{JSON.stringify(change.old)}</span>
                                    {" → "}
                                    <span className="text-ukgreen">{JSON.stringify(change.new)}</span>
                                  </p>
                                ))}
                              </dd>
                            </div>
                          )}
                        </dl>
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-600" data-testid="audit-pagination-info">
          Page {page + 1} of {totalPages} · {total} entries
        </p>
        <div className="flex gap-2">
          <Button data-testid="audit-prev-button" variant="outline" disabled={page === 0} onClick={() => setPage((p) => p - 1)} className="h-11">
            Previous
          </Button>
          <Button data-testid="audit-next-button" variant="outline" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)} className="h-11">
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
